import React, {useState, useEffect} from 'react'
import {Formik, Form, ErrorMessage, useFormikContext} from 'formik'
import * as Yup from 'yup'
import {Modal} from 'react-bootstrap'
import {useSelector, useDispatch, shallowEqual} from 'react-redux'
import {RootState} from '../../../../setup'
import {getTeamSizeList} from '../../../../setup/redux/auth/actions'
import Dialog from '../../../../_metronic/dialogs/CommonDialog'
import {Loader} from '../../../services/Loader'
import Select from 'react-select'
import { customSelectHoverStyles } from '../../../services/Styles'

interface EnterpriseTeamSizeDialogProps {
  show: boolean
  onHide: () => void
  onTeamSizeSelect: (teamSizeId: string) => void
  selectedPlan: any
  selectedType: 'yearly' | 'monthly'
}

interface FormValues {
  team_size_id: string
}

const EnterpriseTeamSizeDialog: React.FC<EnterpriseTeamSizeDialogProps> = ({
  show,
  onHide,
  onTeamSizeSelect,
  selectedPlan,
  selectedType,
}): JSX.Element => {
  const dispatch: any = useDispatch()
  const [teamSizeArray, setTeamSizeArray] = useState<{value: any; label: any}[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const {teamSizeList}: any = useSelector<RootState>(
    (state) => ({
      teamSizeList: state.auth.teamSizeList,
    }),
    shallowEqual
  )

  const validationSchema = Yup.object().shape({
    team_size_id: Yup.string().required('Please select your team size to proceed'),
  })

  const initialValues: FormValues = {
    team_size_id: '',
  }

  useEffect(() => {
    if (teamSizeList.length === 0) {
      dispatch(getTeamSizeList())
    }
  }, [dispatch, teamSizeList.length])

  useEffect(() => {
    if (teamSizeList.length > 0) {
      const teamSizeOptionList = teamSizeList.map((teamsize: any) => ({
        value: teamsize.id,
        label: teamsize.size,
      }))
      setTeamSizeArray(teamSizeOptionList)
    }
  }, [teamSizeList])

  const handleSubmit = async (values: FormValues) => {
    setLoading(true)
    try {
      onTeamSizeSelect(values.team_size_id)
      onHide()
    } catch (error) {
      console.error('Error selecting team size:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    onHide()
  }

  const FormikFormContent = () => {
    const {setFieldValue, values, setFieldTouched, resetForm} = useFormikContext<FormValues>()

    React.useEffect(() => {
      if (!show) {
        resetForm()
      }
    }, [show, resetForm])

    return (
      <Form className='form-box'>
        <div className='mb-3'>
          <label className='form-label'>Please select your team size for the enterprise plan</label>
          <Select
            options={teamSizeArray}
            name='team_size_id'
            menuPosition={'fixed'}
            tabIndex={0}
            value={teamSizeArray.find(option => option.value === values.team_size_id) || null}
            onChange={(option: any) => {
              setFieldValue('team_size_id', option ? option.value : '')
            }}
            onBlur={() => {
              setFieldTouched('team_size_id', true)
            }}

            classNamePrefix='react-custom-select'
            styles={customSelectHoverStyles}
            isDisabled={loading}
            placeholder="Select..."
          />
          <ErrorMessage name='team_size_id' component='div' className='text-danger mt-1' />
        </div>
      </Form>
    )
  }

  const DialogFooter = () => {
    const {isValid, handleSubmit} = useFormikContext<FormValues>()

    return (
      <div className='d-flex align-items-center justify-content-between w-100 gap-7'>
        <button className='btn-import-cancel' onClick={handleCancel}>
          Cancel
        </button>
        <button
          className={`btn-import-confirm ${isValid ? 'enabled' : 'disabled'}`}
          type='submit'
          disabled={!isValid || loading}
          onClick={() => handleSubmit()}
        >
          {loading ? (
            <>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
              Processing...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    )
  }

  return (
    <>
      {loading && <Loader />}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Dialog
          show={show}
          onHide={onHide}
          body={
            <>
              <Modal.Title className='pb-6'>Team Size</Modal.Title>
              <div className='custom-dialog-container pt-8 pb-8 px-1'>
                <FormikFormContent />
              </div>
            </>
          }
          footer={<DialogFooter />}
          modalClassName='custom-reschedule-dialog'
          headerClassname='border-0 pb-0'
          bodyClassname=''
          footerClassname='border-0'
          size='sm'
          centered
          backdrop='static'
          keyboard={false}
          closeButtonDisabled={false}
        />
      </Formik>
    </>
  )
}

export default EnterpriseTeamSizeDialog
